import { useState, useEffect, useCallback, useRef } from "react";
import { usePlaygroundStore } from "@/stores/playgroundStore";

type PermissionState = "granted" | "denied" | "prompt" | "unknown";

interface UseAudioDevicesOptions {
  requestPermissionOnMount?: boolean;
  enableDeviceChangeDetection?: boolean;
  debounceMs?: number;
}

interface UseAudioDevicesReturn {
  audioInputDevices: MediaDeviceInfo[];
  isLoading: boolean;
  error: string | null;
  hasPermission: boolean;
  permissionState: PermissionState;
  refreshDevices: () => Promise<void>;
  requestPermission: () => Promise<boolean>;
}

export function useAudioDevices(
  options: UseAudioDevicesOptions = {}
): UseAudioDevicesReturn {
  const {
    requestPermissionOnMount = false,
    enableDeviceChangeDetection = true,
    debounceMs = 300,
  } = options;

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasPermission, setHasPermission] = useState(false);
  const [permissionState, setPermissionState] =
    useState<PermissionState>("unknown");

  const audioInputDevices = usePlaygroundStore((s) => s.audioInputDevices);
  const setAudioInputDevices = usePlaygroundStore(
    (s) => s.setAudioInputDevices
  );
  const selectedAudioInput = usePlaygroundStore((s) => s.selectedAudioInput);
  const setSelectedAudioInput = usePlaygroundStore(
    (s) => s.setSelectedAudioInput
  );

  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const permissionCheckRef = useRef<boolean>(false);

  // Check if we have microphone permission
  const checkPermission = useCallback(async (): Promise<PermissionState> => {
    try {
      if (!navigator.permissions) {
        setPermissionState("unknown");
        setHasPermission(false);
        return "unknown";
      }

      const result = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });
      const state = (result.state as PermissionState) ?? "unknown";
      setPermissionState(state);
      setHasPermission(state === "granted");
      return state;
    } catch (err) {
      console.warn("Permission check failed:", err);
      setPermissionState("unknown");
      setHasPermission(false);
      return "unknown";
    }
  }, []);

  // Request microphone permission
  const requestPermission = useCallback(async (): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // Clean up the stream immediately - we only needed it for permission
      stream.getTracks().forEach((track) => track.stop());

      setPermissionState("granted");
      setHasPermission(true);
      await enumerateDevices();
      return true;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to access microphone";
      setError(errorMessage);
      setPermissionState("denied");
      setHasPermission(false);
      console.error("Permission request failed:", err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Enumerate devices with proper error handling
  const enumerateDevices = useCallback(async (): Promise<void> => {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) {
        throw new Error("Device enumeration not supported in this browser");
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(
        (device) => device.kind === "audioinput"
      );

      // Remove virtual/pseudo devices and invalid entries
      const filtered = audioInputs.filter(
        (d) =>
          d.deviceId &&
          d.deviceId !== "default" &&
          d.deviceId !== "communications"
      );

      // Dedupe by groupId (prefer entries with labels)
      const dedupeMap = new Map<string, MediaDeviceInfo>();
      for (const d of filtered) {
        const key = d.groupId || d.deviceId;
        const existing = dedupeMap.get(key);
        if (!existing) {
          dedupeMap.set(key, d);
        } else {
          const existingHasLabel =
            !!existing.label && existing.label.trim().length > 0;
          const currentHasLabel = !!d.label && d.label.trim().length > 0;
          if (!existingHasLabel && currentHasLabel) {
            dedupeMap.set(key, d);
          }
        }
      }
      const validDevices = Array.from(dedupeMap.values());

      setAudioInputDevices(validDevices);

      // Repair selection if missing; otherwise auto-select first
      if (validDevices.length > 0) {
        if (
          selectedAudioInput &&
          !validDevices.some((d) => d.deviceId === selectedAudioInput)
        ) {
          setSelectedAudioInput(validDevices[0].deviceId);
        } else if (!selectedAudioInput) {
          setSelectedAudioInput(validDevices[0].deviceId);
        }
      }

      // Infer permission from labels when Permissions API is missing/uncertain
      if (permissionState !== "granted") {
        const hasLabels = validDevices.some(
          (d) => d.label && d.label.trim().length > 0
        );
        if (hasLabels) {
          setPermissionState("granted");
          setHasPermission(true);
        }
      }

      // Clear any previous errors
      setError(null);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to enumerate devices";
      setError(errorMessage);
      console.error("Device enumeration failed:", err);
    }
  }, [
    selectedAudioInput,
    setAudioInputDevices,
    setSelectedAudioInput,
    permissionState,
  ]);

  // Debounced device refresh
  const debouncedRefresh = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      enumerateDevices();
    }, debounceMs);
  }, [enumerateDevices, debounceMs]);

  // Public refresh function
  const refreshDevices = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      await enumerateDevices();
    } finally {
      setIsLoading(false);
    }
  }, [enumerateDevices]);

  // Handle device changes
  useEffect(() => {
    if (!enableDeviceChangeDetection || !navigator.mediaDevices) {
      return;
    }

    const handleDeviceChange = () => {
      console.log("Audio devices changed, refreshing list...");
      debouncedRefresh();
    };

    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        handleDeviceChange
      );
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [enableDeviceChangeDetection, debouncedRefresh]);

  // Refresh when tab becomes visible again (device changes finalize on focus)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        debouncedRefresh();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [debouncedRefresh]);

  // Initial setup
  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);

      try {
        // Check existing permission first
        const state = await checkPermission();

        if (state === "granted" || !requestPermissionOnMount) {
          // If we have permission or don't want to request it, just enumerate
          await enumerateDevices();
        } else if (requestPermissionOnMount) {
          // Request permission and enumerate
          await requestPermission();
          return; // requestPermission already calls enumerateDevices
        }
      } catch (err) {
        setError("Failed to initialize audio devices");
        console.error("Audio devices initialization failed:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (!permissionCheckRef.current) {
      permissionCheckRef.current = true;
      initialize();
    }
  }, [
    checkPermission,
    enumerateDevices,
    requestPermission,
    requestPermissionOnMount,
  ]);

  return {
    audioInputDevices,
    isLoading,
    error,
    hasPermission,
    permissionState,
    refreshDevices,
    requestPermission,
  };
}
